import { JobSourceType, JobSpeciality, JobType, MutationUpdateJobSourceArgs, StartJobInput } from '@clipr/lib';
import { action, computed, makeObservable, observable } from 'mobx';
import isURL from 'validator/lib/isURL';

import { input, inputGroup, InputGroupState, InputState } from '../../components/input';
import { Store } from '../../store/store';
import { BindingProps, StoreNode } from '../../store';
import { AsyncResult } from '../../core';
import isEmpty from 'lodash/isEmpty';
import { EnrichmentItem, generateJobLevelOutput, getDefaultEnrichmentLevel, getDefaultJobLevelInput, getEnrichmentLevelItemTooltip, getEnrichmentLevelLabel, JobLevel } from '../../entities/job/jobFeatures';
import { JobModel } from '../../entities/job';
import { DefaultSpecialityItem, getJobSpecialityInput, getJobSpecialityOutput, isDualPlusPublicSafetySpeciality, parseCustomSpeciality, SpecialityItems, getSpecialityItemsWithCustom, getTeamDefaultJobSpecialityInput } from '../../entities/job/jobSpeciality';
import { DefaultLanguageItem, getDefaultLanguageValue, getLanguageInputItem, LanguageItems } from '../../entities/language';
import { Team, JobVideoTypeList } from '../../entities';

type Props = BindingProps<{
  teamId?: string,
  jobId?: string,
}>;

export type LinkStatus = 'pending' | 'staged' | 'submitted' | 'syncing' | 'error';

export class UploadLinkItemState
  extends StoreNode {

  readonly nodeType = 'UploadLinkItemState';

  constructor(store: Store, props: Props) {
    super(store, props);
    makeObservable(this);
    // submit form

    this.levelInput = input(this, {
      name: 'level',
      placeholder: 'Choose an option',
      selectorItems: () => JobLevel.map(item => ({
        value: item,
        label: getEnrichmentLevelLabel(item),
        tooltip: getEnrichmentLevelItemTooltip(item)
      })),
      showStatusMessage: true,
      isRequired: false,
      statusMessage: (input: InputState) =>
        input.status === 'error' && input.showStatusIcons ? input.error?.toString() : '',
      export: (self: InputState) => self.normValue,
      disabled: () => !!this.jobId
    });

    this.specialityInput = input(this, {
      name: 'speciality',
      placeholder: 'Choose an option',
      selectorItems: () => {
        const uploadPage = this.store.uploadPage;
        return getSpecialityItemsWithCustom(uploadPage.customSpecialties);
      },
      showStatusMessage: true,
      isRequired: () => !this.isSpecialityInputDisabled,
      statusMessage: (input: InputState) =>
        input.status === 'error' && input.showStatusIcons ? input.error?.toString() : '',
      error: (input: InputState) => {
        if (isEmpty(input.value) && !this.isSpecialityInputDisabled) 
          return 'Required field'; },
      export: (self: InputState) => self.normValue,
      disabled: () => !!this.jobId,
      onChange: (input: InputState) => {
        if (input.normValue === 'Medical') {
          const lang = getLanguageInputItem(getDefaultLanguageValue());
          this.languageInput.loadValue(lang);
        }
      }
    });

    this.languageInput = input(this, {
      name: 'language',
      placeholder: 'Choose an option',
      selectorItems: LanguageItems,
      showStatusMessage: true,
      isRequired: true,
      statusMessage: (input: InputState) =>
        input.status === 'error' && input.showStatusIcons ? input.error?.toString() : '',
      error: (input: InputState) => {
        if (isEmpty(input.value)) return 'Required field';
        if (this.specialityInput.normValue === 'Medical' && input.normValue !== 'en-US')
          return 'Only US English supported';
      },
      export: (self: InputState) => self.normValue,
      disabled: () => !!this.jobId || this.specialityInput.normValue === 'Medical'
    });

    this.videoTypeInput = input(this, {
      name: 'videoType',
      placeholder: 'Choose an option',
      selectorItems: JobVideoTypeList,
      showStatusMessage: true,
      export: (self: InputState) => self.normValue,
      disabled: () => !!this.jobId
    });

    this.publicSafetyCaseIdInput = input(this, {
      name: 'publicSafetyCaseId',
      label: 'Case ID',
      placeholder: 'Case ID',
      showStatusMessage: true,
      export: (self: InputState) => self.value?.trim() || undefined,
      disabled: () => !!this.jobId
    });

    this.publicSafetyOfficerNameInput = input(this, {
      name: 'publicSafetyOfficerName',
      label: 'Dual+ Officer Name',
      placeholder: 'Officer name',
      showStatusMessage: true,
      export: (self: InputState) => self.value?.trim() || undefined,
      disabled: () => !!this.jobId
    });

    this.publicSafetyOfficerTitleInput = input(this, {
      name: 'publicSafetyOfficerTitle',
      label: 'Officer Title',
      placeholder: 'Officer title',
      showStatusMessage: true,
      export: (self: InputState) => self.value?.trim() || undefined,
      disabled: () => !!this.jobId
    });

    this.generateDualPlusDraftInput = input(this, {
      name: 'generateDualPlusDraft',
      label: 'Generate Dual+ first draft',
      disabled: () => !!this.jobId
    });

    this.linkInput = input(this, {
      name: 'link',
      placeholder: 'Paste your Video URL here',
      // label: 'Video URL',
      isRequired: true,
      error: (input: InputState) => {
        if (this.isStaged)
          return null;
        if (input.value)
          return this.checkLinkValidity(input.value);

        return null;
      }
    });

    this.hasPasswordInput = input(this, {
      name: 'hasPassword',
      label: "My video is password protected"
    });

    this.passwordInput = input(this, {
      name: 'password',
      placeholder: 'Type in video pwd',
      label: "URL Password",
      error: (input: InputState) => {
        if (this.hasPasswordInput.value && isEmpty(input.value))
          return 'Password required';

        return null;
      }
    });

    this.form = inputGroup(this, {
      name: 'languages',
      inputs: [
        this.specialityInput,
        this.linkInput,
        this.languageInput,
        this.levelInput,
        this.videoTypeInput,
        this.generateDualPlusDraftInput,
        this.publicSafetyCaseIdInput,
        this.publicSafetyOfficerNameInput,
        this.publicSafetyOfficerTitleInput,
        this.passwordInput
      ],
      isSubmitDisabled: () => {
        return (
          this.form.isSubmitting ||
          this.form.hasErrorInputs ||
          this.linkInput.isEmpty ||
          !!(this.hasPasswordInput.value && this.passwordInput.isEmpty)
        );
      }
    });
  }

  @observable status: LinkStatus = 'pending';

  @observable error: unknown = null;

  // submit list
  readonly form: InputGroupState;

  readonly hasPasswordInput: InputState;
  readonly passwordInput: InputState;

  readonly linkInput: InputState;
  readonly languageInput: InputState;
  readonly levelInput: InputState;
  readonly specialityInput: InputState;
  readonly videoTypeInput: InputState;
  readonly generateDualPlusDraftInput: InputState;
  readonly publicSafetyCaseIdInput: InputState;
  readonly publicSafetyOfficerNameInput: InputState;
  readonly publicSafetyOfficerTitleInput: InputState;

  @computed get isPending(): boolean {
    return this.status === 'pending';
  }

  @computed get isStaged(): boolean {
    return this.status === 'staged';
  }

  @computed get isSyncing(): boolean {
    return this.status === 'syncing';
  }

  @computed get isCompleted() {
    return this.status === 'submitted';
  }

  @computed get isFailed() {
    return this.status === 'error';
  }

  @computed get showInStaging(): boolean {
    return this.isSyncing || this.isStaged;
  }

  // #region Resolved props
  @computed get teamId(): string | null {
    return this.resolvedProps.teamId;
  }

  @computed get team(): Team | null {
    return this.store.teamManager.getTeam(this.teamId);
  }

  @computed get jobId(): string | null {
    return this.resolvedProps.jobId;
  }
  // #endregion

  @computed get job() {
    return this.store.jobManager.maybeGetJob(this.jobId);
  }

  @computed get hasDuplicateServerError() {
    const error = this.error as { message?: string } | null;
    return this.isFailed && error?.message === 'Job with Source URL already exists';
  }

  @computed
  get isSpecialityInputDisabled() {
    return this.levelInput?.value === 'media';
  }

  @computed
  get isDualPlusPublicSafetyAvailable() {
    const customParsed = parseCustomSpeciality(this.specialityInput.normValue);
    return isDualPlusPublicSafetySpeciality(customParsed?.speciality ?? this.specialityInput.normValue);
  }

  @computed
  get isPublicSafetyCaseIdVisible() {
    return this.isDualPlusPublicSafetyAvailable && !!this.generateDualPlusDraftInput.value;
  }

  @action
  setStaged() {
    this.status = 'staged';
  }

  @action
  setSubmitted() {
    this.status = 'submitted';
  }

  @action
  setSyncing() {
    this.status = 'syncing';
    this.error = null;
  }

  @action
  setError(err?: unknown) {
    this.status = 'error';
    this.error = err;
  }

  @action
  async submit(submitAsDuplicate: boolean = false): AsyncResult<JobModel> {

    const formData = this.form.export();

    if (this.jobId) {
      const updateJobSourceParams: MutationUpdateJobSourceArgs = {
        jobId: this.jobId,
        source: {
          type: JobSourceType.Url,
          url: this.linkInput.value!.trim(),
          password: this.passwordInput.value || undefined
        }
      }

      this.setSyncing();
      const [job, jobErr] = await this.store.apiUpdateJobSource(updateJobSourceParams);

      if (jobErr) {
        this.setError(jobErr);
        return [null, jobErr];
      }

      this.broadcast('linkSubmitted', { job, teamId: this.teamId, jobId: this.jobId });
      this.setSubmitted();
      return [job!];
    } else {
      const customParsed = parseCustomSpeciality(this.specialityInput.normValue);
      const startJobParams: StartJobInput = {
        source: {
          type: JobSourceType.Url,
          url: formData.link.trim(),
          password: this.passwordInput.value || undefined
        },
        teamId: this.teamId || undefined,
        languageCode: formData.language || undefined,
        forceDuplicate: submitAsDuplicate || undefined,
        features: (formData.level &&
          generateJobLevelOutput(formData.level as EnrichmentItem)) || getDefaultJobLevelInput(),
        medicalSpecialty: getJobSpecialityOutput(formData.speciality) || undefined,
        speciality: customParsed?.speciality ?? this.specialityInput.normValue as JobSpeciality ?? undefined,
        customSpecialtyId: customParsed?.customSpecialtyId ?? undefined,
        publicSafetyCaseId: this.isPublicSafetyCaseIdVisible
          ? this.publicSafetyCaseIdInput.value?.trim() || undefined
          : undefined,
        publicSafetyOfficerName: this.isPublicSafetyCaseIdVisible
          ? this.publicSafetyOfficerNameInput.value?.trim() || undefined
          : undefined,
        publicSafetyOfficerTitle: this.isPublicSafetyCaseIdVisible
          ? this.publicSafetyOfficerTitleInput.value?.trim() || undefined
          : undefined,
        videoType: this.videoTypeInput.value as JobType
      }

      this.setSyncing();
      const [job, jobErr] = await this.store.apiStartJob(startJobParams);

      if (jobErr) {
        this.setError(jobErr);
        return [null, jobErr];
      }

      this.broadcast('linkSubmitted', { job, teamId: this.teamId, jobId: this.jobId });
      this.setSubmitted();
      return [job!];
    }

  }

  checkIsDuplicateLink = (link: string) => {
    const { uploadPage } = this.store;
    const { linkComponent: state } = uploadPage;
    const trimmedLink = (link || '').trim();
    return state.linkInputList.filter(i => (i.linkInput.value || '').trim() === trimmedLink).length > 1;
  }

  checkLinkValidity = (link: string) => {
    const trimmedLink = (link || '').trim();
    const invalidLinkError = 'Link error. Provide a direct video URL.';

    let parsedLink: URL | null = null;
    if (trimmedLink) {
      try {
        parsedLink = new URL(trimmedLink);
      } catch {
        return invalidLinkError;
      }
    }

    // validate_length off: isURL defaults to max_allowed_length 2084, an old IE address-bar
    // limit that has nothing to do with what our downloader can fetch. A presigned S3 URL runs
    // past it on the query string alone -- the signature, credential and session token are ~1.8KB
    // before the path -- so every presigned link was rejected as "not a direct video URL".
    const isValidUrl = isURL(trimmedLink, { protocols: ['http', 'https', 'ftp', 's3'], validate_length: false });
    if (trimmedLink && !isValidUrl)
      return invalidLinkError;

    if (parsedLink) {
      const hostname = parsedLink.hostname.toLowerCase();

      if (
        hostname === 'youtube.com'
        || hostname.endsWith('.youtube.com')
        || hostname === 'youtu.be'
        || hostname === 'youtube-nocookie.com'
        || hostname.endsWith('.youtube-nocookie.com')
      )
        return 'YouTube is not supported.';

      if (hostname === 'twitch.tv' || hostname.endsWith('.twitch.tv'))
        return 'Twitch videos are not supported. Go to https://clipr.xyz.';
    }

    if (this.checkIsDuplicateLink(link))
      return 'Duplicate video link';
    return null;
  }

  stage = () => {
    const { job, team } = this;

    //init specialityInput
    if (job && job.medicalSpecialty !== undefined) {
      this.specialityInput.loadValue(SpecialityItems.find(item =>
        item.value === getJobSpecialityInput(this.job || null)
      ));
    } else {
      const uploadPage = this.store.uploadPage;
      this.specialityInput.loadValue(
        getTeamDefaultJobSpecialityInput(this.team, uploadPage.customSpecialties)
          ?? this.store.user?.defaultJobSpeciality
          ?? DefaultSpecialityItem?.value
      );
    }

    //init languageInput
    if (job?.languageCode) {
      this.languageInput.loadValue(getLanguageInputItem(job.languageCode));
    } else {
      this.languageInput.loadValue(
        getLanguageInputItem(this.team?.defaultTranscriptLanguage ?? null) ?? DefaultLanguageItem
      );
    }

    //init levelInput
    if (job?.enrichmentLevel) {
      this.levelInput.loadValue(JobLevel.find((value) => value === job.enrichmentLevel));
    } else if (team?.enrichmentLevel) {
      this.levelInput.loadValue(JobLevel.find((value) => value === team.enrichmentLevel));
    } else {
      this.levelInput.loadValue(getDefaultEnrichmentLevel());
    }

    //init videoTypeInput
    if (job?.videoType) {
      this.videoTypeInput.loadValue(JobVideoTypeList.find(item => item.value === job.videoType) ?? JobType.Presentation);
    } else {
      this.videoTypeInput.loadValue(JobType.Presentation);
    }

    if (job?.publicSafetyCaseId) {
      this.publicSafetyCaseIdInput.loadValue(job.publicSafetyCaseId);
      this.generateDualPlusDraftInput.loadValue(true);
    }
    if (job?.publicSafetyOfficerName) {
      this.publicSafetyOfficerNameInput.loadValue(job.publicSafetyOfficerName);
      this.generateDualPlusDraftInput.loadValue(true);
    }
    if (job?.publicSafetyOfficerTitle) {
      this.publicSafetyOfficerTitleInput.loadValue(job.publicSafetyOfficerTitle);
      this.generateDualPlusDraftInput.loadValue(true);
    }

    this.setStaged();
  }
}
